// Petit outil d'instrumentation pour identifier les goulots de sync.
// Activation : localStorage.perf = '1' (ou window.__perf = true)
// Tous les logs sont préfixés avec '==**==' pour filtrage console.

const LOG_PREFIX = '==**=='

function isEnabled(): boolean {
   try {
      // @ts-ignore
      if (typeof window !== 'undefined' && (window as any).__perf === true) return true
      return typeof localStorage !== 'undefined' && localStorage.getItem('perf') === '1'
   } catch {
      return false
   }
}

interface Bucket {
   count: number       // nb d'ops
   totalMs: number     // temps cumulé
   bytes: number       // taille cumulée (payload)
}

export interface PerfSession {
   /** Mesure l'exécution d'une promesse dans une catégorie */
   time<T>(category: string, fn: () => Promise<T>): Promise<T>
   /** Ajoute manuellement une durée (ms) à une catégorie */
   add(category: string, ms: number, bytes?: number): void
   /** Ajoute un nombre d'octets observés (payload JSON par ex.) */
   addBytes(category: string, bytes: number): void
   /** Incrémente un compteur dans une catégorie sans ajouter de temps */
   incr(category: string, by?: number): void
   /** Marque un jalon (console.log instantané, préfixé) */
   mark(label: string): void
   /** Termine la session et print le résumé */
   end(): void
   /** Raccourci: vrai si tracking actif */
   enabled: boolean
}

/**
 * Démarre une session de mesure. À appeler au début d'une sync, puis `.end()` à la fin.
 * Si le flag n'est pas activé, retourne un objet no-op (overhead ~0).
 */
export function startPerfSession(label: string): PerfSession {
   const enabled = isEnabled()

   if (!enabled) {
      return {
         enabled: false,
         time: (_c, fn) => fn(),
         add: () => {},
         addBytes: () => {},
         incr: () => {},
         mark: () => {},
         end: () => {},
      }
   }

   const t0 = performance.now()
   const buckets = new Map<string, Bucket>()

   const getBucket = (c: string): Bucket => {
      let b = buckets.get(c)
      if (!b) { b = { count: 0, totalMs: 0, bytes: 0 }; buckets.set(c, b) }
      return b
   }

   console.log(`${LOG_PREFIX} ▶ session '${label}' démarrée`)

   return {
      enabled: true,
      async time<T>(category: string, fn: () => Promise<T>): Promise<T> {
         const b = getBucket(category)
         const s = performance.now()
         try {
            return await fn()
         } finally {
            b.totalMs += performance.now() - s
            b.count += 1
         }
      },
      add(category, ms, bytes = 0) {
         const b = getBucket(category)
         b.totalMs += ms
         b.count += 1
         b.bytes += bytes
      },
      addBytes(category, bytes) {
         getBucket(category).bytes += bytes
      },
      incr(category, by = 1) {
         getBucket(category).count += by
      },
      mark(mlabel) {
         console.log(`${LOG_PREFIX} ⋯ ${((performance.now() - t0) / 1000).toFixed(2)}s — ${mlabel}`)
      },
      end() {
         const totalMs = performance.now() - t0
         const rows: Array<{
            category: string
            ops: number
            total_s: string
            avg_ms: string
            pct: string
            MB: string
            MBps: string
         }> = []
         const sorted = [...buckets.entries()].sort((a, b) => b[1].totalMs - a[1].totalMs)
         for (const [cat, b] of sorted) {
            rows.push({
               category: cat,
               ops: b.count,
               total_s: (b.totalMs / 1000).toFixed(2),
               avg_ms: b.count ? (b.totalMs / b.count).toFixed(2) : '-',
               pct: ((b.totalMs / totalMs) * 100).toFixed(1) + '%',
               MB: b.bytes ? (b.bytes / 1024 / 1024).toFixed(2) : '-',
               MBps: (b.bytes && b.totalMs) ? ((b.bytes / 1024 / 1024) / (b.totalMs / 1000)).toFixed(2) : '-',
            })
         }
         console.log(`${LOG_PREFIX} ■ session '${label}' terminée en ${(totalMs / 1000).toFixed(2)}s`)
         // Log ligne par ligne avec préfixe (filtrable)
         for (const r of rows) {
            console.log(`${LOG_PREFIX}   ${r.category.padEnd(40)} ops=${String(r.ops).padStart(5)}  total=${r.total_s.padStart(7)}s  avg=${r.avg_ms.padStart(7)}ms  ${r.pct.padStart(6)}  MB=${r.MB.padStart(6)}  ${r.MBps !== '-' ? r.MBps + ' MB/s' : ''}`)
         }
         // eslint-disable-next-line no-console
         console.table(rows)
      },
   }
}

/** Estime la taille en octets d'un payload JSON AppWrite (rough, pour tracking) */
export function roughJsonSize(obj: unknown): number {
   try {
      // Tronque à 20MB pour éviter un stringify monstrueux en cas d'accident
      const s = JSON.stringify(obj)
      return s ? s.length : 0   // 1 char ≈ 1 octet ASCII (approximation UTF-8 suffisante pour tracking)
   } catch {
      return 0
   }
}
